import { useCallback } from "react";
import { useAcceptQRPendingOrder } from "@/hooks/useQRPendingOrders";
import { loadAssignments } from "@/hooks/useTPS";
import { printAcceptKots, type AcceptKotPrint } from "@/lib/qrAcceptPrint";
import { neverPaired, rememberOwnRequests } from "@/lib/remotePrint";
import type { QRAcceptPendingOrderResponse } from "@/types/qr-orders";
import type { QRPortalSubmitOrderItem } from "@/types/qr-portal";

export interface AcceptAndPrint {
  result: QRAcceptPendingOrderResponse;
  /** Settles once every KOT job was handed to the print service (or refused). Never rejects. */
  printing: Promise<AcceptKotPrint>;
}

/**
 * Accept a QR order and print the KOTs it sent to the kitchen on this
 * device's KOT printers (the Accept prompt, and Accept / Modify in the panel).
 * The printer assignment is read when Accept is tapped, so a printer assigned
 * after this screen opened is used. Throws what the accept threw.
 */
export function useAcceptAndPrint() {
  const accept = useAcceptQRPendingOrder();
  const { mutateAsync } = accept;

  const acceptAndPrint = useCallback(
    async (id: string, items?: QRPortalSubmitOrderItem[]): Promise<AcceptAndPrint> => {
      // This browser never had the print service (a phone): the outlet's tills print the KOTs.
      const printRemotely = neverPaired();
      const result = await mutateAsync({
        id,
        data: items || printRemotely ? { ...(items ? { items } : {}), ...(printRemotely ? { print_remotely: true } : {}) } : undefined,
      });
      if (printRemotely) {
        rememberOwnRequests(result.print_request_ids);
        const hadKot = !!result.kot_escpos_data || (result.kot_prints?.length ?? 0) > 0;
        return {
          result,
          printing: Promise.resolve({ hadKot, jobs: 0, queued: 0, remote: !!result.remote_print_requested }),
        };
      }
      const printing = printAcceptKots(result, loadAssignments().kot_printer_id).catch(() => ({
        hadKot: true,
        jobs: 0,
        queued: 0,
        remote: false,
      }));
      return { result, printing };
    },
    [mutateAsync],
  );

  return { acceptAndPrint, isPending: accept.isPending };
}
